<template>
  <b-card class="budget-settings mb-3" no-body>
    <b-card-body class="py-3 px-4">
      <div class="budget-settings__row">
        <div class="budget-settings__field">
          <label class="budget-settings__label" for="budget-settings-enforce"> Require a manager budget </label>
          <p class="budget-settings__hint mb-0">
            When enabled, a manager without their own budget can only give instant awards with no reward. When disabled,
            managers without a budget can still give rewarded instant awards unconstrained — managers with a budget are
            always capped by it either way.
          </p>
        </div>
        <b-form-checkbox id="budget-settings-enforce" v-model="localEnforced" switch class="budget-settings__toggle" />
      </div>

      <div class="budget-settings__footer mt-3">
        <b-alert :show="!!saveError" variant="danger" class="mb-2 py-2">{{ saveError }}</b-alert>
        <b-button variant="primary" size="sm" :disabled="isSaving || !isDirty" @click="onSave">
          <b-spinner v-if="isSaving" small class="mr-1" />
          Save
        </b-button>
      </div>
    </b-card-body>
  </b-card>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import State from '@/state/State';
import { updateRecognitionBudgetSettings } from '@/queries/recognition';

const currentEnforced = computed(() =>
  Boolean(State.state.company?.properties?.recognition_properties?.enforce_manager_budgets),
);

// Seed the form from the current server value at mount. The parent modal renders this
// component with `lazy`, so it remounts on every open and this ref always reflects the
// latest persisted setting (see ReportSettings.vue for the same pattern).
const localEnforced = ref(currentEnforced.value);

const isDirty = computed(() => localEnforced.value !== currentEnforced.value);

const emit = defineEmits(['saved']);

const saveError = ref('');

const saveMutation = useMutation(updateRecognitionBudgetSettings());
const isSaving = computed(() => saveMutation.isPending.value);

const onSave = () => {
  saveError.value = '';
  saveMutation.mutate(
    { enforceManagerBudgets: localEnforced.value },
    {
      onSuccess: () => {
        emit('saved');
      },
      onError: () => {
        saveError.value = 'Failed to save settings. Please try again.';
      },
    },
  );
};
</script>

<style lang="scss" scoped>
.budget-settings {
  border: 1px solid var(--palette-color-default-lighten-90);

  &__row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
  }

  &__field {
    flex: 1;
  }

  &__label {
    font-weight: 500;
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
    color: var(--palette-color-default);
  }

  &__hint {
    font-size: 0.8125rem;
    color: var(--palette-color-default-lighten-40);
  }

  &__toggle {
    flex-shrink: 0;
    margin-top: 0.125rem;
  }

  &__footer {
    display: flex;
    align-items: center;
  }
}
</style>
